import { default as HybridConfig } from "lib/HybridConfig";
import Dispatcher from "../Dispatcher";
import OverActions from "../data/OverActions";

const CSRF_PARAM = "_csrf";
const CSRF_HEADER = "X-CSRF-TOKEN";

let noCacheToken = new Date().valueOf();

const addNoCacheToken = url =>
    url + (url.indexOf("?") < 0 ? "?" : "&") + "_=" + (++noCacheToken);

const addNoCacheHeaders = jqXHR => {
    jqXHR.setRequestHeader("Cache-Control", "no-cache");
    jqXHR.setRequestHeader("Pragma", "no-cache");
};

export class BaseServices {
    constructor(dispatcher) {
        this.ajaxSendHandler = this.ajaxSendHandler.bind(this);
        this.ajaxErrorHandler = this.ajaxErrorHandler.bind(this);
        this.ajaxSuccessHandler = this.ajaxSuccessHandler.bind(this);
        this.navigateToAuthUrl = this.navigateToAuthUrl.bind(this);
        this.authUrl = this.authUrl.bind(this);
        this.ensureSession = this.ensureSession.bind(this);
        this.endSession = this.endSession.bind(this);
        this.url = this.url.bind(this);
        this.rawPost = this.rawPost.bind(this);
        this.jsonPost = this.jsonPost.bind(this);
        this.getRequest = this.getRequest.bind(this);

        this.__dispatcher = dispatcher;
        this.root_url = HybridConfig.get("apiRoot", "v2/");
        this.csrfToken = null;
        this.cacheToken = new Date().valueOf();
        this.lastSuccess = null;
        this.reauthInFlight = false;
        this.metaPrefix = this.url("meta/");

        $(document)
            .ajaxSend(this.ajaxSendHandler)
            .ajaxError(this.ajaxErrorHandler)
            .ajaxSuccess(this.ajaxSuccessHandler);
        $(window).bind("beforeunload", function() {
            $(document).unbind("ajaxSend");
            $(document).unbind("ajaxError");
            $(document).unbind("ajaxSuccess");
        });
    }

    ajaxSendHandler(e, jqXHR, ajaxSettings) {
        // cache-bust GETs for non-metamodel JSON
        if (ajaxSettings.type === "GET" && ajaxSettings.dataType === "json" && ajaxSettings.url.indexOf(this.metaPrefix) < 0) {
            addNoCacheHeaders(jqXHR);
            ajaxSettings.url = addNoCacheToken(ajaxSettings.url);
        }
        if (this.csrfToken && !/^(GET|HEAD|TRACE|OPTIONS)$/i.test(ajaxSettings.type)) {
            jqXHR.setRequestHeader(CSRF_HEADER, this.csrfToken);
        }
    }

    ajaxErrorHandler(e, jqXHR, ajaxSettings, thrownError) {
        if (jqXHR.status === 0 && thrownError === "abort") return; // don't care about aborts
        const log = detail => {
            let message = `Received ${jqXHR.status} for '${ajaxSettings.type} ${ajaxSettings.url}'`;
            if (detail) {
                message += "; " + detail;
            }
            console.warn(message);
        };
        if (jqXHR.status === 401) {
            log(this.reauthInFlight
                ? "reauth already in flight"
                : "ask user about reauth");
            this.__dispatcher.dispatch({
                type: OverActions.HTTP_UNAUTHORIZED,
            });
        } else if (ajaxSettings.background) {
            log("[in background]");
        } else if (jqXHR.status === 403) {
            log();
            this.__dispatcher.dispatch({
                type: OverActions.HTTP_FORBIDDEN,
            });
        } else if (jqXHR.status === 404) {
            log();
            this.__dispatcher.dispatch({
                type: OverActions.HTTP_NOT_FOUND,
            });
        } else if (jqXHR.status === 0) {
            if (navigator.onLine) {
                log("browser online; checking 'empty.json'...");
                this.__dispatcher.dispatch({
                    type: OverActions.ARMUS_UNREACHABLE,
                });
                jQuery.ajax({
                    url: "empty.json", // not API relative!
                    global: false,
                    timeout: 3000, // don't wait too long, but allow for "slow"
                    beforeSend: addNoCacheHeaders,
                }).then(
                    () =>
                        log("API dead ('empty.json' is fine)"),
                    jqXHR =>
                        log(`ARMUS unreachable ('empty.json' failed w/ ${jqXHR.status})`),
                );
            } else {
                log("browser offline");
                this.__dispatcher.dispatch({
                    type: OverActions.BROWSER_OFFLINE,
                });
            }
        } else {
            log();
        }
    }

    ajaxSuccessHandler(e, jqXHR, ajaxSettings, data) {
        this.lastSuccess = new Date();
        const token = jqXHR.getResponseHeader(CSRF_HEADER);
        if (token) {
            this.csrfToken = token;
        }
    }

    navigateToAuthUrl() {
        this.reauthInFlight = true;
        window.location.replace(this.authUrl());
    }

    getLastSuccess() {
        return this.lastSuccess;
    }

    authUrl() {
        return this.url(addNoCacheToken(`auth.html${window.location.search}`));
    }

    ensureSession() {
        return $.ajax({
            type: "GET",
            url: this.url(""),
            dataType: "text",
            background: true,
        });
    }

    /*
    This is just _daft_ because of the intersection between the UR un-auth flow,
    CSRF protection, CORS config, and XHR limitations. The important parts are
    that XHR can't be prevented from following redirects, but we can't redirect
    to the UR because of it's CORS, and we can't use a GET because of CSRF. Thus
    we use a magic form which is embedded in the logout link to do a normal form
    POST at the page level to get it done.

    The logout form is provided by AppLayout's template in-DOM, so that it can
    be populated and submitted programmatically here.
    */
    endSession() {
        $("#logout-form")
            .find("input")
            .attr("name", CSRF_PARAM)
            .val(this.csrfToken)
            .end()
            .attr("method", "post")
            .attr("action", this.url("j_spring_security_logout"))
            .submit();
    }

    url(path) {
        if (_.isArray(path)) {
            // strip any leading or trailing slashes before joining
            path = path
                .map(s => {
                    if (typeof s !== "string") return s;
                    while (s.charAt(0) === "/") {
                        s = s.substr(1);
                    }
                    while (s.charAt(s.length - 1) === "/") {
                        s = s.substr(0, s.length - 1);
                    }
                    return s;
                })
                .join("/");
        }

        return this.root_url + path;
    }

    // URL can be either a string or an array. We will prefix the root_url via @url function here.
    rawPost(url, data, settings) {
        if (data == null) { data = {}; }
        url = this.url(url);
        return $.ajax({
            type: "POST",
            url,
            data,
            ...settings,
        });
    }

    jsonPost(url, data, settings) {
        return this.rawPost(url, {}, {
            dataType: "json",
            contentType: "application/json",
            data: JSON.stringify(data),
            ...settings,
        });
    }

    // URL can be either a string or an array. We will prefix the root_url via @url function here.
    getRequest(url, data, settings) {
        return this.rawPost(url, data, {
            type: "GET",
            dataType: "json",
            ...settings
        });
    }
}

export default new BaseServices(Dispatcher);



// WEBPACK FOOTER //
// ./app/lib/BaseServices.js